import * as types from '@/store/mutation-types';
import todoTemplateApi from '@/store/todoItem/todoTemplateApi';
import todoItemApi from '@/store/todoItem/todoItemApi';
import { logAction } from '@/store/logActions';
import { editingAllowed } from '@/store/actions';
import log from '@/logger';

export const loadProjectTodoTemplates = async ({ dispatch, commit }, { projectId }) => {
  let list = await dispatch('getTodoTemplates', { projectId });
  commit(types.setTodoTemplateList, list);
  return list;
};

export const getTodoTemplate = (ctx, id) => {
  log.debug('actions:getTodoTemplate', id);
  return todoTemplateApi.getTodoTemplate(id);
};

export const getTodoTemplates = (ctx, { projectId }) => {
  return todoTemplateApi.getTodoTemplates(projectId);
};

export const deleteTodoTemplate = async ({ commit, state }, todoTemplate) => {
  log.debug('actions:deleteTodoTemplate', todoTemplate);

  if (!editingAllowed(state)) {
    return;
  }
  logAction({ commit, state }, { project: state.currentProject, object: todoTemplate, reason: 'Deleted TodoTemplate' });
  commit(types.setTodoTemplateProperties, {
    updateTodoTemplate: todoTemplate,
    newTodoTemplateProperties: { archived: true },
  });
  await todoTemplateApi
    .saveTodoTemplate(todoTemplate)
    .then((savedTodoTemplate) => {
      commit(types.setTodoTemplateProperties, {
        updateTodoTemplate: todoTemplate,
        newTodoTemplateProperties: savedTodoTemplate,
      });
    })
    .catch((e) => {
      log.error('todoTemplateActions:deleteTodoTemplate, saving TodoTemplate', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const addTodoTemplate = ({ commit, state }, { projectId, newTodoTemplateProperties }) => {
  if (!editingAllowed(state)) {
    return;
  }

  return todoTemplateApi
    .createTodoTemplate(projectId, newTodoTemplateProperties)
    .then((todoTemplate) => {
      // setTodoTemplateProperties({commit, state}, {updateTodoTemplate: todoTemplate, newTodoTemplateProperties: newTodoTemplateProperties});
      commit(types.addTodoTemplate, { newTodoTemplate: todoTemplate });
      logAction(
        { commit, state },
        { project: state.currentProject, object: todoTemplate, reason: 'Added TodoTemplate' }
      );

      return todoTemplate;
    })
    .catch((e) => {
      log.error('todoTemplateActions:addTodoTemplate', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const saveTodoTemplateItemProperties = ({ dispatch }, itemUpdate) => {
  log.debug('actions:saveTodoTemplateItemProperties', itemUpdate);

  let update = {
    updateTodoTemplate: itemUpdate.updateTodoTemplate,
    newTodoTemplateProperties: {
      todoTemplates: JSON.parse(JSON.stringify(itemUpdate.updateTodoTemplate.todoTemplates)),
    },
  };

  if (itemUpdate.hasOwnProperty('newIndex')) {
    update.newTodoTemplateProperties.todoTemplates.splice(
      itemUpdate.newIndex,
      0,
      update.newTodoTemplateProperties.todoTemplates.splice(itemUpdate.index, 1)[0]
    );
    for (let i = 0; i < update.newTodoTemplateProperties.todoTemplates.length; i++) {
      update.newTodoTemplateProperties.todoTemplates[i].sort = i;
    }
  } else {
    update.newTodoTemplateProperties.todoTemplates[itemUpdate.index] = Object.assign(
      {},
      itemUpdate.updateTodoTemplate.todoTemplates[itemUpdate.index],
      itemUpdate.newTodoTemplateItemProperties
    );
  }
  console.log('update to ', update);
  return dispatch('saveTodoTemplateProperties', update);
};

export const saveTodoTemplateProperties = ({ commit, state }, update) => {
  log.debug('actions:saveTodoTemplateProperties', update);

  if (!editingAllowed(state)) {
    return Promise.resolve();
  }

  logAction(
    { commit, state },
    {
      project: state.currentProject,
      object: update.updateTodoTemplate,
      reason: 'Updated TodoTemplate Properties',
      update: update.newTodoTemplateProperties,
    }
  );

  setTodoTemplateProperties({ commit, state }, update);

  return todoTemplateApi
    .saveTodoTemplate(update.updateTodoTemplate)
    .then((savedTodoTemplate) => {
      update.newTodoTemplateProperties = savedTodoTemplate;

      commit(types.setTodoTemplateProperties, update);
      return savedTodoTemplate;
    })
    .catch((e) => {
      log.error('Error when saving todoTemplate', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const setTodoTemplateProperties = ({ commit, state }, update) => {
  if (!editingAllowed(state)) {
    return;
  }

  commit(types.setTodoTemplateProperties, update);
};

export const addTodoTemplateFromItems = async (
  { commit, dispatch, state },
  { projectId, partId, newTodoTemplateProperties }
) => {
  if (!editingAllowed(state)) {
    return;
  }
  if (!newTodoTemplateProperties) {
    newTodoTemplateProperties = {};
  }

  if (!newTodoTemplateProperties.title) {
    newTodoTemplateProperties.title = state.parts[partId].title;
  }

  let items = await dispatch('getTodoItems', { projectId, partId });
  newTodoTemplateProperties.todoTemplates = JSON.parse(JSON.stringify(items, ['title', 'sort']));

  return todoTemplateApi
    .createTodoTemplate(projectId, newTodoTemplateProperties)
    .then((todoTemplate) => {
      // commit(types.addTodoTemplate, {newTodoTemplate: todoTemplate});
      logAction(
        { commit, state },
        { project: state.currentProject, object: todoTemplate, reason: 'Created TodoTemplate From Items' }
      );
      return todoTemplate;
    })
    .catch((e) => {
      log.error('todoTemplateActions:addTodoTemplate', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const addTodoItemsFromTemplate = async ({ dispatch, state }, { projectId, templateId, partId }) => {
  if (!editingAllowed(state)) {
    return;
  }

  let tpl = await dispatch('getTodoTemplate', templateId);

  let items = [];
  let baseSort = (state.selectedPartTodoItems || []).length;
  for (let idx = 0; idx < tpl.todoTemplates.length; idx++) {
    let newTodoItemProperties = tpl.todoTemplates[idx];
    if (!newTodoItemProperties.archived) {
      newTodoItemProperties.sort = baseSort + newTodoItemProperties.sort;
      items.push(todoItemApi.newTodoItem(projectId, partId, newTodoItemProperties));
    }
  }
  await todoItemApi.saveTodoItems(items);
};

export const syncTodoTemplate = ({ commit, state }, todoTemplate) => {
  if (todoTemplate.projectId === state.currentProject._id) {
    let foundItem = state.currentProjectTodoTemplates.find((i) => {
      return i._id === todoTemplate._id;
    });

    if (foundItem) {
      commit(types.setTodoTemplateProperties, {
        updateTodoTemplate: foundItem,
        newTodoTemplateProperties: todoTemplate,
      });
    } else {
      commit(types.addTodoTemplate, { newTodoTemplate: todoTemplate });
    }
  }
};



// WEBPACK FOOTER //
// ./src/store/todoItem/todoTemplateActions.js